"use client";

import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useCallback,
} from "react";
import { CheckCircle, X, Plus, Minus, ShoppingCart } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCart } from "@/contexts/cart-context";
import { debounce } from "@/libs/utils";
import { envConfig } from "@/libs/env-config";
import { createCheckoutApi } from "@/api/checkout";
import { CartStorage } from "@/libs/cart-storage";
import { convertPaisaToRupees, fireUserEngagementEvent } from "@/libs/utils";
import {
  type ICheckoutStartedEvent,
  EventType,
  publishEvent,
  type ICheckoutCompletedEvent,
  type IAddPaymentInfoEvent,
} from "@/events";
import { GuestUserStorage } from "@/libs/guest-user-storage";

declare global {
  interface Window {
    gokwikSdk?: {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      initCheckout: (config: any) => void;
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      on: (event: string, callback: (data: any) => void) => void;
      close?: () => void;
    };
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    TrackierWebSDK?: any;
  }
}

export const CartDrawer: React.FC = () => {
  const router = useRouter();
  const {
    items,
    isOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    subtotal,
    clearCart,
    error,
  } = useCart();

  const [isAgeVerified, setIsAgeVerified] = useState(false);
  const [localQuantities, setLocalQuantities] = useState<
    Record<string, number>
  >({});

  // // Initialize local quantities from cart items
  // useEffect(() => {
  //   const quantities: Record<string, number> = {};
  //   items.forEach(item => {
  //     quantities[item.variant_id] = item.quantity;
  //   });
  //   setLocalQuantities(quantities);
  // }, [items]);

  // const debouncedApiCall = useCallback(
  //   debounce((variantId: string, quantity: number) => {
  //     updateQuantity(variantId, quantity);
  //   }, 300),
  //   [updateQuantity]
  // );

  // const handleQuantityChange = (variantId: string, newQuantity: number) => {
  //   // Update local state immediately
  //   setLocalQuantities(prev => ({
  //     ...prev,
  //     [variantId]: newQuantity
  //   }));
  //   // Debounce the API call
  //   // debouncedApiCall(variantId, newQuantity);
  // };
  const [showThankYouPopup, setShowThankYouPopup] = useState(false);
  const eventListenersAttached = React.useRef(false);

  // Manage document body overflow and handle checkout popup close events
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;

    // Set overflow when cart opens/closes
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalStyle;
    }

    // Handle message events from checkout popup
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const onCheckoutPopupClose = (event: any) => {
      const messageType = event?.data;

      if (messageType === "close" && isOpen) {
        requestAnimationFrame(() => {
          document.body.style.overflow = "hidden";
        });
      }
    };

    window.addEventListener("message", onCheckoutPopupClose);

    // Cleanup function
    return () => {
      document.body.style.overflow = originalStyle;
      window.removeEventListener("message", onCheckoutPopupClose);
    };
  }, [isOpen]);

  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     const handleOrderSuccess = (event: MessageEvent) => {
  //       console.log("Received message:", event.data.type);

  //       if (event?.data?.type === "orderSuccess") {
  //         const orderId =
  //           event.data.response?.order_name ||
  //           event.data.response.orderSuccessBody?.order_name;
  //         const orderAmount =
  //           event.data.response?.total_price ||
  //           event.data.response.orderSuccessBody?.total_price;

  //         if (
  //           typeof window !== "undefined" &&
  //           window.TrackierWebSDK &&
  //           orderId &&
  //           orderAmount
  //         ) {
  //           window.TrackierWebSDK.trackConv(
  //             "track.techtrack.in",
  //             "6776449c8e7dea356102ea94",
  //             {
  //               is_camp: true,
  //               txn_id: orderId,
  //               sale_amount: orderAmount,
  //               is_iframe: true,
  //             }
  //           );
  //         }
  //       }
  //     };

  //     window.addEventListener("message", handleOrderSuccess);

  //     return () => {
  //       window.removeEventListener("message", handleOrderSuccess);
  //     };
  //   }
  // }, []);

  const initGoKwik = () => {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    if (typeof window !== "undefined" && (window as any).gokwikSdk) {
      const userPhone = ""; // You should populate this with the user's phone number if available

      const payload = {
        environment: envConfig.gokwik.environment,
        type: envConfig.gokwik.type,
        mid: envConfig.gokwik.mid,
        phoneNumber: userPhone,
        bannerMessages: envConfig.gokwik.bannerMessages,
        merchantParams: {
          merchantCheckoutId: CartStorage.getCheckoutId() || "",
          customerToken: "", // You may need to provide this value
          origReferrer: window.location.href,
          adSource: "",
          landingPage: window.location.origin,
        },
      };

      try {
        if (window.gokwikSdk) {
          window.gokwikSdk.initCheckout(payload);
        }
      } catch (error) {
        console.error("Error initializing GoKwik checkout:", error);
      }
    } else {
      console.error("GoKwik SDK not loaded");
    }
  };

  const triggeredCheckoutStatedEvent = () => {
    const event: ICheckoutStartedEvent = {
      type: EventType.CHECKOUT_STARTED,
      cartValue: convertPaisaToRupees(subtotal),
      currency: "INR",
      timestamp: Date.now(),
      itemCount: items.length,
      items: items.map((item) => ({
        productId: item.product_id,
        productName: item.product_title,
        price: convertPaisaToRupees(item.price),
        quantity: item.quantity,
        variant: item.variant_title,
      })),
    };
    publishEvent(event);
  };

  interface Order {
    merchant_order_id?: string;
    customer?: {
      id?: string;
    };
  }

  const triggeredCheckoutCompleteEvent = (order: Order) => {
    const event: ICheckoutCompletedEvent = {
      type: EventType.CHECKOUT_COMPLETED,
      orderId: order?.merchant_order_id || "",
      cartValue: convertPaisaToRupees(subtotal),
      currency: "INR",
      itemCount: items.length,
      timestamp: Date.now(),
      items: items.map((item) => ({
        productId: item.id,
        productName: item.product_title || item.title,
        price: convertPaisaToRupees(item.price),
        quantity: item.quantity,
        variant: item.variant_title || "",
      })),
    };
    // Publish the checkout completed event
    publishEvent(event);
  };

  interface PaymentInfoData {
    payment_method?: string;
  }

  const triggeredAddPaymentInfoEvent = (data: PaymentInfoData) => {
    const event: IAddPaymentInfoEvent = {
      type: EventType.ADD_PAYMENT_INFO,
      timestamp: Date.now(),
      cartValue: convertPaisaToRupees(subtotal),
      currency: "INR",
      itemCount: items.length,
      paymentType: data?.payment_method || "unknown",
      items: items.map((item) => ({
        productId: item.id,
        productName: item.product_title || item.title,
        price: convertPaisaToRupees(item.price),
        quantity: item.quantity,
        variant: item.variant_title || "",
      })),
    };

    publishEvent(event);
  };

  const attachCheckoutEvent = () => {
    if (window.gokwikSdk && !eventListenersAttached.current) {
      // Mark that we've attached listeners to avoid duplicates
      eventListenersAttached.current = true;
      fireUserEngagementEvent();

      // Listen for various completion events
      window.gokwikSdk.on(
        "order-complete",
        (order: { merchant_order_id?: string; customer?: { id?: string } }) => {
          console.log("order-complete event received:", order);

          // Clear checkout ID and cart data from localStorage
          CartStorage.clear();
          // Clear cart state in the application
          clearCart();
          // Close the cart drawer
          closeCart();
          if (window.gokwikSdk?.close) {
            window.gokwikSdk.close();
          }

          // KP tracking is now handled by the KpAdapter

          triggeredCheckoutCompleteEvent(order);

          // Get the order ID from the response or generate a temporary one
          const orderId = order?.merchant_order_id || `temp-${Date.now()}`;
          // Redirect to the order confirmation page
          router.push(`/${order?.customer?.id || "guest"}/orders/${orderId}`);
        },
      );

      window.gokwikSdk.on(
        "payment-success",
        (data: {
          payment_method: string;
          amount: number;
          transaction_id: string;
        }) => {
          console.log("payment-success event received:", data);

          // Clear checkout ID and cart data from localStorage
          CartStorage.clear();
          // Clear cart state in the application
          clearCart();
          // Close the cart drawer
          closeCart();

          triggeredAddPaymentInfoEvent(data);
        },
      );

      window.gokwikSdk.on(
        "checkout-complete",
        (data: {
          orderId?: string;
          status?: string;
          [key: string]: unknown;
        }) => {
          console.log("checkout-complete event received:", data);
          // Clear checkout ID and cart data from localStorage
          CartStorage.clear();
          // Clear cart state in the application
          clearCart();
          // Close the cart drawer
          closeCart();
        },
      );

      window.gokwikSdk.on(
        "user-login-successful",
        (data: { userId: string; userName: string; email?: string }) => {
          console.log("user-login-successful event received:", data);
          GuestUserStorage.saveGuestUserData(data);
        },
      );
    }
  };

  const onCheckout = async () => {
    triggeredCheckoutStatedEvent();
    // KP tracking is now handled by the KpAdapter
    try {
      const response = await createCheckoutApi();

      if (response?.id) {
        attachCheckoutEvent();
        initGoKwik();
      }
    } catch (error) {
      console.error("Checkout error", error);
    }
  };

  const closeThankYouPopup = () => {
    setShowThankYouPopup(false);
  };

  const loyaltyPoints = Math.floor((subtotal / 100) * 0.2);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={closeCart}
      />

      {/* Cart drawer */}
      <div className="absolute inset-y-0 right-0 max-w-full flex justify-end">
        <div className="relative w-[90%] sm:w-screen sm:max-w-md transform transition-transform duration-300 ease-in-out origin-right">
          <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto border-l border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 ">
              <h2 className="text-[24px] font-system text-[#000000] font-bold text-center w-full">
                CART
              </h2>
              <button
                id="close-cart-button"
                type="button"
                onClick={closeCart}
                className="text-[red] absolute right-4"
                aria-label="Close cart"
              >
                <X className="h-8 w-8" />
              </button>
            </div>

            {/* Cart items */}
            <div className="flex-1 px-4 overflow-auto">
              {items.length === 0 ? (
                <div className="text-center flex flex-col items-center justify-center h-full py-20">
                  <h3 className="text-[17px] font-Poppins p-4 text-[#000000]">
                    Your cart is currently empty.
                  </h3>
                </div>
              ) : (
                <>
                  <div className="h-[1px] bg-gray-200 my-4" />
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div
                        key={`${item.id}-${item.variant_title || ""}-${index}`}
                        className="border-b pb-4"
                      >
                        <div className="flex items-start">
                          <div className="h-20 w-20 flex-shrink-0 overflow-hidden border border-gray-200">
                            <Image
                              src={
                                item.featured_image?.url ||
                                item.image ||
                                "/images/placeholder.png"
                              }
                              alt={
                                item.product_title ||
                                item.title ||
                                "Product image"
                              }
                              width={64}
                              height={64}
                              loading="lazy"
                              className="h-full w-full object-cover object-center"
                            />
                          </div>

                          <div className="ml-4 flex-1">
                            <div className="flex justify-between">
                              <h3 className="text-[17px] font-Poppins text-[#000000] ">
                                {item.product_title || item.title}
                              </h3>
                            </div>
                            <p className="mt-1 text-[15.3px] font-Poppins text-[#000000] ">
                              {item.variant_title && (
                                <>Pack Size: {item.variant_title}</>
                              )}
                            </p>
                            <div className="flex items-center justify-between mt-4">
                              <div className="relative">
                                {error && (
                                  <div className="absolute -top-8 left-0 bg-red-100 text-red-600 text-xs p-2 rounded-md whitespace-nowrap">
                                    {error}
                                  </div>
                                )}
                                <div className="flex items-center border p-1">
                                  <button
                                    id="decrease-quantity-button"
                                    aria-label="Decrease quantity"
                                    className="p-1 px-2"
                                    data-test-id="decrease-quantity"
                                    onClick={() => {
                                      fireUserEngagementEvent();
                                      updateQuantity(
                                        item.variant_id,
                                        item.quantity - 1,
                                      );
                                    }}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  <span className="px-2">
                                    {localQuantities[item.variant_id] ||
                                      item.quantity}
                                  </span>
                                  <button
                                    id="increase-quantity-button"
                                    aria-label="Increase quantity"
                                    className={`p-1 px-2 ${
                                      error
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    data-test-id="increase-quantity"
                                    onClick={() => {
                                      if (!error) {
                                        fireUserEngagementEvent();
                                        updateQuantity(
                                          item.variant_id,
                                          (localQuantities[item.variant_id] ||
                                            item.quantity) + 1,
                                        );
                                      }
                                    }}
                                    disabled={!!error}
                                    title={error || ""}
                                  >
                                    <Plus
                                      className={`h-4 w-4 ${
                                        error ? "text-gray-400" : ""
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>

                              <p className="text-base font-medium text-gray-900">
                                Rs. {(item.price / 100 || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-gray-200 px-4 py-4">
                {/* Age verification */}
                <div className="mb-4 p-3 shadow-md bg-white rounded-md text-center">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      id="age-verification"
                      data-test-id="age-verification-checkbox"
                      checked={isAgeVerified}
                      onChange={() => setIsAgeVerified(!isAgeVerified)}
                      className="h-3 w-3 md:h-4 md:w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="age-verification"
                      className="ml-2 text-[16px] md:text-[20px] font-semibold font-Poppins text-black"
                    >
                      Are you above 18+?
                    </label>
                  </div>
                  <p className="text-[12px] font-Poppins text-[#ff0000] text-center mt-1">
                    Please tick before proceeding
                  </p>
                </div>

                {/* Subtotal */}
                <div className="flex justify-between mb-2">
                  <p className="text-[20px] font-system text-[#202020]">
                    Subtotal
                  </p>
                  <p className="text-[20px] font-Poppins text-gray-900">
                    Rs. {(subtotal / 100).toFixed(2)}
                  </p>
                </div>

                {/* Loyalty points */}
                <div
                  className="border border-[black] rounded-md p-3 mb-4 text-center"
                  data-test-id="loyalty-points"
                >
                  <div className="flex justify-center items-center">
                    <span className="font-semibold text-[#000000] text-[14px] font-Poppins">
                      Earn{" "}
                    </span>
                    <span className="inline-flex items-center bg-orange-100 text-orange-800 px-2 rounded-full font-bold mx-1">
                      <span className="text-xs mr-1">🔥</span>
                      <span>{loyaltyPoints}</span>
                    </span>
                    <span className="font-semibold text-[#000000] text-[14px] font-Poppins">
                      {" "}
                      Coins on this Cart
                    </span>
                  </div>
                  <p className="font-semibold text-[#000000] text-[11px] font-Poppins mt-3">
                    POPCoins Loyalty Discount will be applied on checkout
                  </p>
                </div>

                <p className="text-[11.7px] font-Poppins text-[#000000] text-center mb-3">
                  Shipping, taxes, and discount codes calculated at checkout.
                </p>

                {/* Checkout button */}
                <div className="w-full">
                  <button
                    id="checkout-button"
                    type="button"
                    onClick={onCheckout}
                    disabled={!isAgeVerified}
                    className={`w-full flex flex-col items-center justify-center rounded-md border border-transparent ${
                      isAgeVerified
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-gray-300 cursor-not-allowed"
                    } px-6 py-3 text-base font-medium text-white shadow-sm relative`}
                    data-test-id="checkout-button"
                    aria-label="Proceed to Checkout"
                  >
                    <div className="flex items-center space-x-2">
                      <span>Checkout</span>
                      <Image
                        src="/images/ppg.png"
                        alt="Payment Options"
                        width={100}
                        height={32}
                        loading="lazy"
                        className="h-8 w-[100px]"
                      />
                    </div>
                    {/* Text below Checkout and logo */}
                    <p className="text-xs text-gray-200 mt-1">
                      5% off on prepaid orders
                    </p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
